import { useState, useEffect } from 'react'
import { X, Loader } from 'lucide-react'
import type { DashboardReview, ReviewStatus } from '../../types/dashboard'
import { isQualityOnlyEscalation } from '../../types/dashboard'

interface ReviewDetailPanelProps {
  review: DashboardReview | null
  onClose: () => void
  onApprove: (reviewId: string, responseText: string) => Promise<void>
  onReject: (reviewId: string) => Promise<void>
}

const MAX_CHARS = 4096

const SENTIMENT_CLASSES: Record<string, string> = {
  positive: 'bg-rd-accent/10 text-rd-accent',
  neutral: 'bg-warning-500/10 text-warning-500',
  negative: 'bg-danger-500/10 text-danger-500',
}

const STATUS_CLASSES: Record<ReviewStatus, string> = {
  pending: 'bg-white/5 text-rd-muted',
  processing: 'bg-rd-accent2/10 text-rd-accent2',
  pending_approval: 'bg-warning-500/10 text-warning-500',
  responded: 'bg-rd-accent/10 text-rd-accent',
  escalated: 'bg-danger-500/10 text-danger-500',
  failed: 'bg-danger-500/10 text-danger-500',
  skipped: 'bg-white/5 text-rd-dim',
}

const STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  pending_approval: 'Pending approval',
  responded: 'Posted',
  escalated: 'Escalated',
  failed: 'Failed',
  skipped: 'Skipped',
}

// A quality-only escalation (e.g. a 5-star review whose auto-generated reply
// came out too short) isn't a bad review — it shouldn't get the same red
// "something's wrong" treatment as a genuine negative-review escalation.
const QUALITY_ONLY_LABEL = 'Needs a quick look'
const QUALITY_ONLY_CLASS = 'bg-warning-500/10 text-warning-500'

function statusLabel(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_LABEL : STATUS_LABELS[review.status]
}
function statusClass(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_CLASS : STATUS_CLASSES[review.status]
}

function Stars({ rating }: { rating: number | null }) {
  if (!rating) return null
  return (
    <span className="flex items-center gap-px text-sm">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? 'text-warning-500' : 'text-rd-dim'}>★</span>
      ))}
    </span>
  )
}

export interface PanelToast {
  message: string
  type: 'success' | 'danger' | 'info'
}

interface ReviewDetailPanelPropsWithToast extends ReviewDetailPanelProps {
  onToast: (toast: PanelToast) => void
}

export default function ReviewDetailPanel({
  review,
  onClose,
  onApprove,
  onReject,
  onToast,
}: ReviewDetailPanelPropsWithToast) {
  const [responseText, setResponseText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  useEffect(() => {
    if (review) {
      setResponseText(review.generated_response ?? '')
      setIsEditing(false)
    }
  }, [review?.id])

  const isOpen = review !== null
  const canAct = review && review.status !== 'responded' && review.status !== 'skipped' && review.status !== 'processing'
  const alreadyPosted = review?.status === 'responded'

  const platformLabel = review?.platform === 'judgeme' ? 'Judge.me' : 'Google'

  const handleApprove = async () => {
    if (!review) return
    setIsApproving(true)
    try {
      await onApprove(review.id, responseText)
      onToast({ message: `✓ Reply posted to ${platformLabel} successfully`, type: 'success' })
      onClose()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to post reply', type: 'danger' })
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    if (!review) return
    setIsRejecting(true)
    try {
      await onReject(review.id)
      onToast({ message: 'Review moved to manual queue', type: 'info' })
      onClose()
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to reject review', type: 'danger' })
    } finally {
      setIsRejecting(false)
    }
  }

  const initials = review?.reviewer_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? '?'

  const sentiment = review?.intent_analysis?.sentiment
  const severity = review?.escalation_result?.severity

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 w-[420px] h-screen bg-rd-surface border-l border-white/[0.08] flex flex-col z-50 transition-transform duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {review && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.08] flex-shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[14px] font-medium text-rd-text">{review.reviewer_name ?? 'Anonymous'}'s review</p>
                  {review.platform === 'judgeme' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500/10 text-primary-500">
                      Judge.me
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-rd-dim mt-0.5">
                  {review.review_date
                    ? new Date(review.review_date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                    : 'Date unknown'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-white/[0.08] text-rd-muted hover:bg-rd-surface2 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">

              {/* Status badges */}
              <div>
                <p className="text-[10px] text-rd-dim uppercase tracking-wider mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {sentiment && sentiment in SENTIMENT_CLASSES && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${SENTIMENT_CLASSES[sentiment]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                    </span>
                  )}
                  {severity && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/5 text-rd-muted">
                      {severity.charAt(0).toUpperCase() + severity.slice(1)} severity
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass(review)}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {statusLabel(review)}
                  </span>
                </div>
              </div>

              {/* Original review */}
              <div>
                <p className="text-[10px] text-rd-dim uppercase tracking-wider mb-2">Original review</p>
                <div className="bg-rd-surface2 border border-white/[0.07] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary-500/15 border border-primary-500/20 flex items-center justify-center text-[11px] font-medium text-primary-500">
                        {initials}
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-rd-text">{review.reviewer_name ?? 'Anonymous'}</p>
                      </div>
                    </div>
                    <Stars rating={review.rating} />
                  </div>
                  <p className="text-[13px] text-rd-muted leading-relaxed">
                    {review.review_text ?? '(No review text)'}
                  </p>
                </div>
              </div>

              {/* AI response */}
              <div>
                <p className="text-[10px] text-rd-dim uppercase tracking-wider mb-2">AI-generated response</p>
                <div className="bg-rd-accent/[0.04] border border-rd-accent/20 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-rd-accent animate-pulse" />
                    <span className="text-[10px] text-rd-accent uppercase tracking-wider font-medium">ReputeDesk AI draft</span>
                  </div>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    disabled={!isEditing || alreadyPosted}
                    rows={6}
                    maxLength={MAX_CHARS}
                    className="w-full bg-transparent border-none outline-none text-rd-muted text-[13px] leading-relaxed resize-none disabled:cursor-default"
                    placeholder={alreadyPosted ? 'Response already posted.' : '(No AI response generated for this review)'}
                  />
                  <p className={`text-[10px] text-right mt-1 font-mono ${responseText.length > 3800 ? 'text-warning-500' : 'text-rd-dim'}`}>
                    {responseText.length} / {MAX_CHARS}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            {!alreadyPosted && canAct && (
              <div className="flex flex-col gap-2 px-5 py-4 border-t border-white/[0.08] flex-shrink-0">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isApproving || isRejecting || !responseText.trim()}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium bg-rd-accent/15 border border-rd-accent/30 text-rd-accent hover:bg-rd-accent/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isApproving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {isApproving ? 'Posting…' : `Post reply to ${platformLabel}`}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setIsEditing(true) }}
                    disabled={isApproving || isRejecting || isEditing}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[13px] font-medium bg-primary-500/10 border border-primary-500/25 text-primary-500 hover:bg-primary-500/15 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleReject}
                  disabled={isApproving || isRejecting}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium bg-danger-500/8 border border-danger-500/20 text-danger-500 hover:bg-danger-500/12 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isRejecting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : (
                    <X className="w-3.5 h-3.5" />
                  )}
                  {isRejecting ? 'Rejecting…' : 'Reject — send to manual queue'}
                </button>
              </div>
            )}

            {alreadyPosted && (
              <div className="px-5 py-4 border-t border-white/[0.08] flex-shrink-0">
                <p className="text-center text-[12px] text-rd-accent">
                  ✓ Reply posted to {platformLabel}
                  {review.response_posted_at && (
                    <span className="text-rd-dim ml-1">
                      · {new Date(review.response_posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
